import type { SupabaseClient } from '@supabase/supabase-js';

export interface TodoItemCore {
    id: string;
    user_id: string;
    title: string;
    description?: string | null;
    location?: string | null;
    due_date: string | null; // ISO or null
    done: boolean;
    contact_id?: string | null;
    event_id?: string | null;
    assigned_contact_id?: string | null;
    group_id?: string | null;
    recurrence?: string | null; // 'daily', 'weekly', 'monthly', 'yearly'
    reminder_minutes?: number | null; // minutes before event
    created_at?: string;
    updated_at?: string;
}

function isMissingTodosTable(err: any) {
    const msg = String(err?.message || err?.details || err);
    return /relation .*todos.* does not exist/i.test(msg) || /table .*todos.* does not exist/i.test(msg);
}

function isMissingTodosColumn(err: any, column: string) {
    const msg = String(err?.message || err?.details || err);
    // Example: Could not find the 'description' column of 'todos' in the schema cache
    return /Could not find the '.*' column of 'todos' in the schema cache/i.test(msg) && msg.includes(`'${column}'`);
}

export async function listTodosCore(
    supabase: SupabaseClient,
    userId: string,
    opts?: { includeCompleted?: boolean; from?: string; to?: string; groupId?: string | null; assignedContactId?: string | null; contactId?: string | null }
) {
    let q = supabase
        .from('todos')
        .select('*')
        .order('due_date', { ascending: true, nullsFirst: false })
        .order('created_at', { ascending: false })
        // Always scope to current user; RLS likely enforces this, but be explicit to avoid empty reads
        .eq('user_id', userId);

    // Within the current user's todos, optionally filter by group context: include null (global) or matching group
    if (opts?.groupId) q = q.or(`group_id.eq.${opts.groupId},group_id.is.null`);

    if (opts?.assignedContactId) {
        q = q.eq('assigned_contact_id', opts.assignedContactId);
    }

    if (opts?.contactId) q = q.eq('contact_id', opts.contactId);
    if (opts?.includeCompleted === false) q = q.eq('done', false);
    if (opts?.from) q = q.gte('due_date', opts.from);
    if (opts?.to) q = q.lte('due_date', opts.to);

    const { data, error } = await q;
    if (error) {
        if (isMissingTodosTable(error)) {
            throw new Error('Todos feature not initialized. Apply migration 20250816_create_todos.sql');
        }
        if (isMissingTodosColumn(error, 'description')) {
            throw new Error("Todos feature upgrade required. Apply migration 20250817_extend_todos_for_calendar.sql to add 'description' and related columns.");
        }
        throw error;
    }
    return (data || []) as TodoItemCore[];
}

export async function createTodoCore(
    supabase: SupabaseClient,
    input: Omit<TodoItemCore, 'id' | 'created_at' | 'updated_at' | 'user_id'>,
    userId: string
) {
    const { data, error } = await supabase
        .from('todos')
        .insert({ ...input, user_id: userId })
        .select('*')
        .single();
    if (error) {
        if (isMissingTodosTable(error)) throw new Error('Todos feature not initialized. Apply migration 20250816_create_todos.sql');
        if (isMissingTodosColumn(error, 'description')) throw new Error("Todos feature upgrade required. Apply migration 20250817_extend_todos_for_calendar.sql to add 'description' and related columns.");
        throw error;
    }
    return data as TodoItemCore;
}

export async function updateTodoCore(supabase: SupabaseClient, id: string, patch: Partial<TodoItemCore>) {
    const { data, error } = await supabase
        .from('todos')
        .update({ ...patch, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select('*')
        .single();
    if (error) {
        if (isMissingTodosTable(error)) throw new Error('Todos feature not initialized. Apply migration 20250816_create_todos.sql');
        throw error;
    }
    return data as TodoItemCore;
}

export async function deleteTodoCore(supabase: SupabaseClient, id: string) {
    const { error } = await supabase.from('todos').delete().eq('id', id);
    if (error) {
        if (isMissingTodosTable(error)) throw new Error('Todos feature not initialized. Apply migration 20250816_create_todos.sql');
        throw error;
    }
}

export async function getTodoByIdCore(supabase: SupabaseClient, id: string) {
    const { data, error } = await supabase.from('todos').select('*').eq('id', id).maybeSingle();
    if (error) {
        if (isMissingTodosTable(error)) throw new Error('Todos feature not initialized. Apply migration 20250816_create_todos.sql');
        throw error;
    }
    return data as TodoItemCore | null;
}

export async function listTodosByEventIdsCore(supabase: SupabaseClient, eventIds: string[]): Promise<Pick<TodoItemCore, 'id' | 'event_id'>[]> {
    if (!Array.isArray(eventIds) || eventIds.length === 0) return [];
    const { data, error } = await supabase
        .from('todos')
        .select('id, event_id')
        .in('event_id', eventIds as any);
    if (error) {
        if (isMissingTodosTable(error)) throw new Error('Todos feature not initialized. Apply migration 20250816_create_todos.sql');
        throw error;
    }
    return (data || []) as any;
}

// Bulk-assign any of a user's unassigned todos to a specific contact (e.g., "My Contact").
export async function assignUnassignedTodosToContactCore(
    supabase: SupabaseClient,
    userId: string,
    contactId: string
): Promise<void> {
    if (!userId) throw new Error('assignUnassignedTodosToContactCore: userId is required');
    if (!contactId) throw new Error('assignUnassignedTodosToContactCore: contactId is required');
    const { error } = await supabase
        .from('todos')
        .update({ assigned_contact_id: contactId })
        .eq('user_id', userId)
        .is('assigned_contact_id', null);
    if (error) {
        if (isMissingTodosTable(error)) throw new Error('Todos feature not initialized. Apply migration 20250816_create_todos.sql');
        throw error;
    }
}

// Recent todos for a user, ordered by created_at desc
export async function listRecentTodosCore(
    supabase: SupabaseClient,
    userId: string,
    limit: number = 6
): Promise<Array<Pick<TodoItemCore, 'id' | 'title' | 'created_at' | 'due_date' | 'contact_id'>>> {
    if (!userId) throw new Error('listRecentTodosCore: userId is required');
    const { data, error } = await supabase
        .from('todos')
        .select('id, title, created_at, due_date, contact_id')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);
    if (error) {
        if (isMissingTodosTable(error)) throw new Error('Todos feature not initialized. Apply migration 20250816_create_todos.sql');
        throw error;
    }
    return (data || []) as any;
}
